import React, { useState, useEffect } from "react";

import { Col, Row, Table, Button, notification, Spin } from "antd";
import { SaveFilled, AppstoreFilled } from '@ant-design/icons';

import { useNavigate, useSearchParams } from 'react-router-dom';

import { getEvaluations, getEvaluationByCode, updateEvaluations, getExecutionTypes, getFrequencys, getStatus  } from "../../API/evaluation";

import Columns, { isDisabled } from "./components/EvaluationColumns/columns";
import EvaluationSearch from './components/EvaluationSearch/EvaluationSearch';

import WithPermission from "../Common/WithPermission/WithPermission";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from "../Common/PageTitle/PageTitle";
import ChangeLog from "../Common/ChangeLog/ChangeLog";
import Assetia from "../Common/Assetia/Assetia";

import { useAuth } from "../Context/AuthContext";

const Evaluation = () => {
  const navigate = useNavigate();
  // Permisos
  const { hasPermission, userData, logout } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;
  // Consulta permisos: Tiene rol asignado  
  const hasAnyRole = userData?.roles?.length > 0;
  // Consulta especifica de permisos
  const canEvaluationRead = userData ? hasPermission("evaluation", "read") : false;
  //const canEvaluationUpdate = userData ? hasPermission("evaluation", "update") : false;

  const [api, contextHolder] = notification.useNotification();
  const [controls, setControls] = useState([]);
  const [originalControls, setOriginalControls] = useState(new Map());
  const [editedControls, setEditedControls] = useState(new Set());
  // Loader
  const [loading, setLoading] = useState(true);
  // Buscador
  const [filteredData, setFilteredData] = useState([]); 
  const [searchText, setSearchText] = useState('');
  // Opciones
  const [executionTypes, setExecutionTypes] = useState([]);
  const [frequencies, setFrequencies] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  // Filtro de status inicial (Pendiente)

  // Redirección desde el dashboard
  const [searchParams, setSearchParams] = useSearchParams();
  const dashboardStatusFilter = searchParams.get('status');
  const dashboardStageFilter = searchParams.get('stage');
  //const [internalFilters, setInternalFilters] = useState({});
  // Obtener los filtros del Dashboard de la URL
  const assetsFromUrl = searchParams.get('assets')?.split(',').filter(Boolean) || [];
  const areasFromUrl = searchParams.get('areas')?.split(',').filter(Boolean) || [];
  //
  const [activeFilters, setActiveFilters] = useState({});
  // Detectar si los filtros vienen del dashboard
  const hasUrlFilters = assetsFromUrl.length > 0 || areasFromUrl.length > 0 || dashboardStatusFilter || dashboardStageFilter;

  // Función para manejar cambios en la tabla y trackear filtros
  const handleTableChange = (pagination, filters, sorter, extra) => {
    // Guardar filtros activos
    setActiveFilters(filters);
    
    // Solo modificar URL si hay filtros del dashboard
    if (hasUrlFilters) {
      const newParams = new URLSearchParams();
      
      if (filters.status?.length > 0) {
        newParams.set('status', filters.status[0]);
      }
      if (filters.asset?.length > 0) {
        newParams.set('assets', filters.asset.join(','));
      }
      if (filters.areas?.length > 0) {
        newParams.set('areas', filters.areas.join(','));
      }
      // Agregar stage si existe en los filtros
      const stageFilter = Object.entries(filters).find(([key]) => 
        ['definido', 'diseñado', 'implementado', 'validado', 'documentado'].includes(key)
      );
      if (stageFilter) {
        newParams.set('stage', stageFilter[0]);
      }
      
      setSearchParams(newParams);
    }
    // Si NO hay filtros del dashboard, NO tocar la URL
  };

  useEffect(() => {
    const initialFilters = {};
    
    if (assetsFromUrl.length > 0) {
      initialFilters.asset = assetsFromUrl;
    }
    
    if (areasFromUrl.length > 0) {
      initialFilters.areas = areasFromUrl;
    }
    
    if (dashboardStatusFilter) {
      initialFilters.status = [dashboardStatusFilter];
    }
    
    if (dashboardStageFilter) {
      initialFilters[dashboardStageFilter.toLowerCase()] = [true];
    }
    
    // Solo setear si hay filtros del dashboard
    if (Object.keys(initialFilters).length > 0) {
      setActiveFilters(initialFilters);
    }
  }, [assetsFromUrl.join(','), areasFromUrl.join(','), dashboardStatusFilter, dashboardStageFilter]);
  
  useEffect(() => {
    fetchInitialData();
  }, []);

  const mapControl = (control, options) => {
    // Buscar los nombres correspondientes a los IDs
    const statusOption = options.statusOptions.find(opt => opt.id === control.status);
    const frequencyOption = options.frequencies.find(opt => opt.id === control.frequency);
    const executionTypeOption = options.executionTypes.find(opt => opt.id === control.execution_type);

    return {
      area: control.area || null,
      code: control.code,
      definited: control.definited,
      designed: control.designed,
      implemented: control.implemented,
      validated: control.validated,
      documented: control.documented,
      initialStatus: statusOption?.name,
      status: statusOption?.name,
      status_id: control.status,
      frequency: frequencyOption?.name,
      frequency_id: control.frequency,
      executionType: executionTypeOption?.name,
      execution_type_id: control.execution_type,
      assetName: control.dependency_control?.dependency?.asset?.name || "Sin nombre de activo",
      dependencyName: control.dependency_control?.dependency?.name || "Sin dependencia",
      controlTitle: control.dependency_control?.control?.title || "Sin control",
      controlDescription: control.dependency_control?.control?.description || "Sin descripción",
      can_evaluate: control.can_evaluate ?? false,
      documentUrl: control.document_file_url || null
    };
  };
  
  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const [evaluationsRes, executionRes, frequencyRes, statusRes] = await Promise.all([
        getEvaluations(),
        getExecutionTypes(),
        getFrequencys(),
        getStatus()
      ]);

      setExecutionTypes(executionRes);
      setFrequencies(frequencyRes);
      setStatusOptions(statusRes);

      const options = {
        executionTypes: executionRes,
        frequencies: frequencyRes,
        statusOptions: statusRes
      };

      // Mapear TODOS los controles de una vez
      const allControls = evaluationsRes.map(control => mapControl(control, options));
      setControls(allControls);

      // Si hay filtros del dashboard, aplicarlos
      let initialFilteredData = allControls;
      
      if (areasFromUrl.length > 0 || assetsFromUrl.length > 0) {
        if (areasFromUrl.length > 0) {
          initialFilteredData = initialFilteredData.filter(item => 
            item.area && areasFromUrl.includes(item.area.name)
          );
        }
        
        if (assetsFromUrl.length > 0) {
          initialFilteredData = initialFilteredData.filter(item => 
            assetsFromUrl.includes(item.assetName)
          );
        }
      }
      
      setFilteredData(initialFilteredData);

      // Guardar originales
      const initialMap = new Map();
      allControls.forEach(control => {
        initialMap.set(control.code, { ...control });
      });
      setOriginalControls(initialMap);

      setLoading(false);

    } catch (error) {
      api.error({ message: "Error al cargar los datos" });
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const controlsToSave = controls.filter(control => {
      const originalControl = originalControls.get(control.code);
       const documentChanged =
        (control.document_file_url && control.document_file_url instanceof File) ||
        (originalControl.document_file_url !== control.document_file_url) || 
        (originalControl.documentUrl !== control.documentUrl);     
      return (
        originalControl &&
        (originalControl.designed !== control.designed ||
        originalControl.implemented !== control.implemented ||
        originalControl.validated !== control.validated ||
        originalControl.documented !== control.documented ||
        originalControl.frequency_id !== control.frequency_id ||
        originalControl.execution_type_id !== control.execution_type_id ||
        originalControl.status_id !== control.status_id) ||
        documentChanged
      );
    });

    if (controlsToSave.length === 0) {
      api.info({ 
        message: "No se han realizado cambios en las evaluaciones",
        key: 'change-info'
      });
      return;
    }

    const hasMissingFields = controlsToSave.some(control => {
      const isEnabled = !isDisabled(control) && control.status !== "Finalizada";
      if (!isEnabled) return false;

      if (control.designed) {
        return !control.frequency_id || !control.execution_type_id;
      }
      return false;
    });

    if (hasMissingFields) {
      api.error({ 
        message: "Error al guardar", 
        description: "Debes completar Tipo de Ejecución y Frecuencia cuando el control está Diseñado" 
      });
      return;
    }

    const hasMissingDocuments = controlsToSave.some(control => control.documented && !(control.uploadedFile || control.documentUrl));
    if (hasMissingDocuments) {
      api.error({
        message: "Error al guardar",
        description: "Debes subir el documento para todos los controles Documentados",
        key: 'save-evaluation-error'
      });
      return;
    }

    try {
      await Promise.all(
        controlsToSave.map(async (control) => {
          const { code, assetName, dependencyName, controlTitle, controlDescription, uploadedFile, ...data } = control;

          // Creamos FormData para enviar los datos y el archivo
          const formData = new FormData();

          // Agregamos todos los campos primitivos al FormData
          Object.entries(data).forEach(([key, value]) => {
            if (value !== undefined && value !== null && (typeof value !== 'object')) {
              formData.append(key, value);
            }
          });

          // Agregamos el archivo real si existe
          if (uploadedFile) {
            formData.append("document_file", uploadedFile);
          }

          // Llamamos al backend con todo junto
          await updateEvaluations(code, formData);
        })
      );

      setEditedControls(new Set());
      api.success({ message: "Datos guardados exitosamente" });
      fetchInitialData(); 
    } catch (error) {
      api.error({ 
        message: "Error al guardar los datos",
        description: error.response?.data?.message || error.message,
        key: 'save-evaluation-error'
      });
    }
  };

  // Modal + Log
  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getEvaluationByCode(record.code);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs", /* description: error.message */ });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  const evaluationTableFilter = () => {
    // Función helper para aplicar filtros
    const applyFilter = (data, filterKey, filterValues) => {
      if (!filterValues || filterValues.length === 0) return data;
      
      const filterMap = {
        asset: (control) => control.assetName,
        areas: (control) => control.area?.name,
        status: (control) => control.initialStatus === 'Evaluado' ? 'Evaluado' : 'Pendiente',
        definido: (control) => control.definited,
        diseñado: (control) => control.designed,
        implementado: (control) => control.implemented,
        validado: (control) => control.validated,
        documentado: (control) => control.documented,
      };
      
      const getter = filterMap[filterKey];
      if (!getter) return data;
      
      return data.filter(control => {
        const value = getter(control);
        return filterValues.includes(value);
      });
    };

    // Obtener datos filtrados excluyendo una columna
    const getFilteredDataForColumn = (excludeColumn) => {
      let data = [...controls];
      
      Object.entries(activeFilters).forEach(([key, values]) => {
        if (key !== excludeColumn && values && values.length > 0) {
          data = applyFilter(data, key, values);
        }
      });
      
      return data;
    };

    // Generar opciones dinámicas
    const assetsData = getFilteredDataForColumn('asset');
    const uniqueAssets = Array.from(
      new Set(assetsData.map(c => c.assetName).filter(Boolean))
    ).sort();

    const areasData = getFilteredDataForColumn('areas');
    const uniqueArea = Array.from(
      new Map(
        areasData
          .filter(c => c.area)
          .map(c => [c.area.id, c.area])
      ).values()
    ).sort((a, b) => a.name.localeCompare(b.name));

    return {
      dashboardStatusFilter: dashboardStatusFilter || null,
      dashboardStageFilter: dashboardStageFilter || null,
      isDashboardStageFilter: !!dashboardStageFilter,
      assetFilterOptions: uniqueAssets,
      areaFilterOptions: uniqueArea,
      dashboardAssetsFilter: assetsFromUrl ?? [],
      dashboardAreasFilter: areasFromUrl ?? [],
    };
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      hasAnyRole={hasAnyRole}
      requiredPermission="read"
      currentPermission={canEvaluationRead}
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="evaluation-container">
          <Row justify="start" className="page-header">
            <Col span={16}>
              <PageTitle title="Evaluación de Controles" icon="evaluation" />
            </Col>
            <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <EvaluationSearch 
                evaluationData={controls} 
                setFilteredData={setFilteredData} 
                searchText={searchText} 
                setSearchText={setSearchText} 
              />
            </Col>
          </Row>

          <Row justify="end" gutter={[8, 0]} style={{ marginBottom: 16 }}>
            {
              isSuperuser &&
              <Col>
                <Button 
                  type="primary"
                  className="view-deleted-button"
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (assetsFromUrl.length) params.set('assets', assetsFromUrl.join(','));
                    if (areasFromUrl.length) params.set('areas', areasFromUrl.join(','));
                    navigate(`/evaluation-deleted${params.toString() ? `?${params.toString()}` : ''}`);
                  }}>
                  <AppstoreFilled />
                  Ver Borrados
                </Button>
              </Col>
            }
            <Col>
              <ExportButton
                api={api}
                contextHolder={contextHolder}
                data={filteredData}
                filename="Evaluacion.csv"
                transformData={(data) =>
                  data.map(item => {
                    const execution = executionTypes.find(e => e.id === item.execution_type_id)?.name || 'N/A';
                    const frequency = frequencies.find(f => f.id === item.frequency_id)?.name || 'N/A';
                    
                    return {
                    Activo: item.assetName || 'N/A',
                    Dependencia: item.dependencyName || 'N/A',
                    Control: item.controlTitle || 'N/A',
                    Área: item.area?.name || 'N/A',
                    Status: statusOptions.find(s => s.id === item.status_id)?.name || 'N/A',
                    Definido: item.definited ? 'Sí' : 'No',
                    Diseñado: item.designed ? 'Sí' : 'No',
                    Implementado: item.implemented ? 'Sí' : 'No',
                    Validado: item.validated ? 'Sí' : 'No',
                    Documentado: item.documented ? 'Sí' : 'No',
                    'Ejecución y Frecuencia': `${execution} - ${frequency}`,
                   }
                 })
                }
              />
            </Col>
          </Row>

          <Row justify="end" style={{ marginBottom: "16px" }}>
            <Col>
              <Button
                icon={<SaveFilled />}
                className="save-type-button" 
                onClick={handleSave}
                /* disabled={
                  !(
                    isSuperuser ||
                    (hasAnyRole && canEvaluationUpdate)
                  )
                } */
              >
                Guardar
              </Button>
            </Col>
          </Row>

          <Spin className="custom-spin" spinning={loading}>
            <Table
              className="custom-pagination"
              dataSource={filteredData}
              columns={Columns(controls, setControls, setEditedControls, {
                executionTypes,
                frequencies,
                statusOptions
              }, evaluationTableFilter(), handleOpenChangeLog, api)}
              scroll={{ y: 450 }}
              rowKey="code"
              pagination={{
                defaultPageSize: 30,
                showSizeChanger: true,
                pageSizeOptions: ['30', '50', '100'],
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`

              }}
              onChange={handleTableChange}
              rowClassName={(record) => (!record.can_evaluate ? "disabled-row" : "")}
              //filters={internalFilters}
            />
          </Spin>

          <ChangeLog
            open={changeLogVisible}
            onClose={handleCloseChangeLog}
            data={changeLogData}
            logs={changeLogLogs}
          />

          <Row justify="end" style={{ marginTop: "16px" }}>
            <Col>
              <Button
                icon={<SaveFilled />}
                className="save-type-button" 
                onClick={handleSave}
                /* disabled={
                  !(
                    isSuperuser ||
                    (hasAnyRole && canEvaluationUpdate)
                  )
                } */
              >
                Guardar
              </Button>
            </Col>
          </Row>
        </div>
        <Row style={{ marginTop: 80 }}>
          <Assetia
            pageTitle="evaluación"
            pageDescription="podras evaluar cada asignación de manera efectiva"
            view="evaluation"
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default Evaluation;